import { Box, Button } from '@mui/material';
import { AxiosResponse } from 'axios';
import LoadingSpinner from 'components/loading-spinner';
import { useAccountState } from 'contexts/account/account-context';
import saveAs from 'file-saver';
import { useGet } from 'hooks/use-get';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useJsonToCsv } from 'react-json-csv';
import { useHistory, useParams } from 'react-router-dom';
import { UserType } from 'types/shared/user-type.enum';
import { getJsonFromBlob } from 'utils/get-json-from-blob';
import { getScenarioInputFile, getScenarioOutputFile } from 'views/bluviu-nutrition/features/scenarios/api/bluviu-nutrition';
import { useScenarioState } from 'views/bluviu-nutrition/features/scenarios/context/scenario-context';
import { ScenarioState } from 'views/bluviu-nutrition/types/scenario-state.enum';
import { ScenariosParams } from 'views/bluviu-nutrition/features/scenarios/types/scenarios-params.type';
const PAGE_NAME_ID = 'bluViuNutrition.setUp';

type Props = {};

const ScenarioActions: React.FC<Props> = ({}) => {
  const { account } = useAccountState();
  const params = useParams<ScenariosParams>();
  const { saveAsCsv } = useJsonToCsv();
  const history = useHistory();

  const {
    scenarioQuery: { data: scenario, isLoading: isScenarioLoading },
  } = useScenarioState();

  const saveScenarioFile = async (response: AxiosResponse, exportAsCsv: boolean) => {
    const disposition = response.headers['content-disposition']?.split(';');
    const fn = disposition?.find((n: string) => n.includes('filename='));
    if (fn) {
      const filename = `${scenario?.name.replace(/ /g, '_')}_${fn.replace('filename=', '').replace(/"/gi, '').trim()}`;
      if (!exportAsCsv) {
        saveAs(response.data, filename, { autoBom: false });
      } else {
        const outputJson = await getJsonFromBlob(response.data);
        saveAsCsv({
          data: outputJson,
          fields: {
            productionWeek: 'productionWeek',
            programCode: 'programCode',
            idealProteinNitrogen: 'idealProteinNitrogen',
            nonIdealProteinNitrogen: 'nonIdealProteinNitrogen',
            digestibleFat: 'digestibleFat',
            weightStartOfWeek: 'weightStartOfWeek',
            temperature: 'temperature',
            latitude: 'latitude',
            dayOfYear: 'dayOfYear',
            feedIntake: 'feedIntake',
            weightGainDaily: 'weightGainDaily',
            carcassYieldRaw: 'carcassYieldRaw',
            carcassYieldStartOfWeek: 'carcassYieldStartOfWeek',
            guttedWeightStartOfWeek: 'guttedWeightStartOfWeek',
            dcy: 'dcy',
            guttedWeightEndOfWeek: 'guttedWeightEndOfWeek',
            carcassYieldEndOfWeek: 'carcassYieldEndOfWeek',
            feedConversionRatio: 'feedConversionRatio',
            fig: 'fig',
            cfig: 'cfig',
            fatStartOfWeek: 'fatStartOfWeek',
            fatEndOfWeek: 'fatEndOfWeek',
            price: 'price',
            feedCostPerUnitGrowth: 'feedCostPerUnitGrowth',
            currency: 'currency',
            globalWarmingPotentialDailyIncrement: 'globalWarmingPotentialDailyIncrement',
            feedPlan: 'feedPlan',
            daysToReach: 'daysToReach',
            carcassYieldPercent: 'carcassYieldPercent',
            weightGainGrams: 'weightGainGrams',
          },
          filename: `${filename.replace('.json', '')}`,
        });
      }
    }
  };

  const downloadInputFileQuery = useGet(
    ['getScenarioInputFile', account.company?.companyId, scenario?.id],
    ({ queryKey }) => getScenarioInputFile(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: false,
    }
  );

  const downloadOutputFileQuery = useGet(
    ['getScenarioOutputFile', account.company?.companyId, scenario?.id],
    ({ queryKey }) => getScenarioOutputFile(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: false,
    }
  );

  const handleDownloadFiles = async () => {
    const inputFile = await downloadInputFileQuery.refetch();
    if (inputFile.isSuccess && inputFile.data) await saveScenarioFile(inputFile.data, false);

    const outputFile = await downloadOutputFileQuery.refetch();
    if (outputFile.isSuccess && outputFile.data) await saveScenarioFile(outputFile.data, false);
  };

  const handleDownloadCsv = async () => {
    const outputFile = await downloadOutputFileQuery.refetch();
    if (outputFile.isSuccess && outputFile.data) await saveScenarioFile(outputFile.data, true);
  };

  const canExport = useMemo(() => {
    console.log('export buttons re-render');

    if (!scenario) return false;
    return (
      account.company?.features.hasFormulationCost &&
      account.currentUser?.userType == UserType.SuperUser &&
      (scenario.state === ScenarioState.COMPLETED || scenario.state === ScenarioState.SCENARIO_RESULT_SENT)
    );
  }, [account, scenario]);

  const resultsReady = useMemo(() => {
    if (!scenario) return false;

    return scenario.state === ScenarioState.SCENARIO_RESULT_SENT;
  }, [scenario]);

  if (isScenarioLoading) return <LoadingSpinner />;
  if (!scenario) return;

  return (
    <Box display={'flex'} flexDirection={'row'} gap={4} justifyContent={'flex-end'}>
      {canExport && (
        <>
          <Button onClick={() => handleDownloadFiles()}>
            <FormattedMessage id="general.exportScriptsBtn" />
          </Button>
          <Button onClick={() => handleDownloadCsv()}>
            <FormattedMessage id="general.exportCSVBtn" />
          </Button>
        </>
      )}
      {resultsReady && (
        <Button
          onClick={() =>
            history.push({
              pathname: `/bluviu-nutrition/scenarios/${params.globalSiteId}/${params.scenarioId}/result`,
              state: { siteName: scenario.name },
            })
          }
          size="small">
          <FormattedMessage id={`${PAGE_NAME_ID}.viewResultsBTN`} />
        </Button>
      )}
    </Box>
  );
};

export default React.memo(ScenarioActions);
